import { Helmet } from "react-helmet-async";
import { SEO_CONFIG, buildAbsoluteUrl, buildCanonicalUrl } from "./utils";

interface SeoProps {
  title?: string;
  description: string;
  path?: string;
  keywords?: string[];
  image?: string;
  imageAlt?: string;
  ogTitle?: string;
  ogDescription?: string;
  type?: "website" | "article";
  schema?: Record<string, unknown> | Array<Record<string, unknown>>;
  noIndex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
}

function Seo({
  title,
  description,
  path = "/",
  keywords,
  image = SEO_CONFIG.defaultImage,
  imageAlt = SEO_CONFIG.defaultImageAlt,
  ogTitle,
  ogDescription,
  type = "website",
  schema,
  noIndex = false,
  publishedTime,
  modifiedTime,
}: SeoProps) {
  const canonicalUrl = buildCanonicalUrl(path);
  const resolvedTitle = title || SEO_CONFIG.defaultTitle;
  const robots = noIndex
    ? "noindex, nofollow"
    : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
  const schemas = Array.isArray(schema) ? schema : schema ? [schema] : [];
  const resolvedImage = buildAbsoluteUrl(image);
  const resolvedOgTitle = ogTitle || resolvedTitle;
  const resolvedOgDescription = ogDescription || description;

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en-GB" />
      <title>{resolvedTitle}</title>
      <meta name="description" content={description} />
      {keywords?.length ? (
        <meta name="keywords" content={keywords.join(", ")} />
      ) : null}
      <meta name="author" content={SEO_CONFIG.author} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en-gb" href={canonicalUrl} />
      <meta property="og:locale" content={SEO_CONFIG.locale} />
      <meta property="og:site_name" content={SEO_CONFIG.siteName} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={resolvedOgTitle} />
      <meta property="og:description" content={resolvedOgDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SEO_CONFIG.twitterHandle} />
      <meta name="twitter:creator" content={SEO_CONFIG.twitterHandle} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={resolvedOgTitle} />
      <meta name="twitter:description" content={resolvedOgDescription} />
      <meta name="twitter:image" content={resolvedImage} />
      <meta name="twitter:image:alt" content={imageAlt} />
      {type === "article" && publishedTime ? (
        <meta property="article:published_time" content={publishedTime} />
      ) : null}
      {type === "article" && modifiedTime ? (
        <meta property="article:modified_time" content={modifiedTime} />
      ) : null}
      {schemas.map((item, index) => (
        <script key={`schema-${index}`} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
}

export default Seo;
